import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { PersonStanding, X, Plus, Minus, RotateCcw, Contrast, Link2, Type, Pause, MousePointer2, AlignJustify } from "lucide-react";
import { useFocusTrap } from "./a11y/useFocusTrap";

const KEY = "upv_a11y_prefs";
const DEFAULTS = { fontStep: 0, contrast: false, links: false, readfont: false, readspace: false, nomotion: false, cursor: false };
const ZOOM = [1, 1.1, 1.2, 1.35, 1.5];

const load = () => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") }; }
  catch { return { ...DEFAULTS }; }
};

const apply = (p) => {
  const el = document.documentElement;
  el.classList.toggle("a11y-contrast", p.contrast);
  el.classList.toggle("a11y-links", p.links);
  el.classList.toggle("a11y-readfont", p.readfont);
  el.classList.toggle("a11y-readspace", p.readspace);
  el.classList.toggle("a11y-nomotion", p.nomotion);
  el.classList.toggle("a11y-cursor", p.cursor);
  el.style.zoom = String(ZOOM[p.fontStep] || 1);
};

const Toggle = ({ icon: Icon, label, active, onClick, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={active}
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-3 w-full rounded-xl border px-4 py-3 text-left text-[15px] font-medium transition-colors ${
      active ? "border-[#d90429] bg-[#d90429]/5 text-[#d90429]" : "border-slate-200 bg-white text-[#0b192c] hover:border-slate-300"
    }`}
  >
    <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
    <span className="flex-1">{label}</span>
    <span aria-hidden="true" className={`text-xs font-bold ${active ? "text-[#d90429]" : "text-slate-400"}`}>{active ? "ON" : "OFF"}</span>
  </button>
);

export const AccessibilityWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(load);
  const panelRef = useRef(null);

  useEffect(() => { apply(prefs); localStorage.setItem(KEY, JSON.stringify(prefs)); }, [prefs]);
  useFocusTrap(panelRef, open, () => setOpen(false));

  if (location.pathname === "/admin") return null;

  const set = (patch) => setPrefs((p) => ({ ...p, ...patch }));
  const toggle = (k) => set({ [k]: !prefs[k] });
  const reset = () => setPrefs({ ...DEFAULTS });
  const anyActive = prefs.fontStep > 0 || prefs.contrast || prefs.links || prefs.readfont || prefs.readspace || prefs.nomotion || prefs.cursor;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed z-50 left-5 bottom-24 lg:bottom-6 w-14 h-14 rounded-full bg-[#0b192c] text-white shadow-[0_8px_30px_rgba(11,25,44,0.35)] flex items-center justify-center hover:bg-[#1a2a40] transition-colors"
        aria-label={open ? "Close accessibility options" : "Open accessibility options"}
        aria-expanded={open}
        aria-controls="a11y-panel"
        data-testid="a11y-widget-toggle"
      >
        <PersonStanding className="w-7 h-7" aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            id="a11y-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby="a11y-panel-title"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-50 left-4 right-4 sm:right-auto bottom-40 lg:bottom-24 sm:w-[360px] max-h-[72vh] bg-white rounded-2xl shadow-[0_20px_60px_rgba(11,25,44,0.28)] border border-black/10 flex flex-col overflow-hidden"
            data-testid="a11y-panel"
          >
            <div data-a11y-dark className="bg-[#0b192c] text-white px-5 py-4 flex items-center justify-between">
              <div>
                <h2 id="a11y-panel-title" className="font-display font-bold text-lg">Accessibility options</h2>
                <p className="text-xs text-white/70">Your choices are saved on this device.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close accessibility options" data-testid="a11y-panel-close" className="text-white/80 hover:text-white p-1">
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f8f9fa]">
              {/* Text size */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="text-[15px] font-medium text-[#0b192c] flex items-center gap-3"><Type className="w-5 h-5" aria-hidden="true" /> Text size</span>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => set({ fontStep: Math.max(0, prefs.fontStep - 1) })} disabled={prefs.fontStep === 0} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:border-[#d90429] disabled:opacity-40" aria-label="Decrease text size" data-testid="a11y-text-decrease"><Minus className="w-4 h-4" aria-hidden="true" /></button>
                    <span className="w-10 text-center text-sm font-semibold text-[#0b192c]" aria-live="polite" data-testid="a11y-text-level">{Math.round((ZOOM[prefs.fontStep] || 1) * 100)}%</span>
                    <button type="button" onClick={() => set({ fontStep: Math.min(ZOOM.length - 1, prefs.fontStep + 1) })} disabled={prefs.fontStep === ZOOM.length - 1} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:border-[#d90429] disabled:opacity-40" aria-label="Increase text size" data-testid="a11y-text-increase"><Plus className="w-4 h-4" aria-hidden="true" /></button>
                  </div>
                </div>
              </div>

              <Toggle icon={Contrast} label="Boost contrast" active={prefs.contrast} onClick={() => toggle("contrast")} testid="a11y-contrast-toggle" />
              <Toggle icon={Link2} label="Highlight links" active={prefs.links} onClick={() => toggle("links")} testid="a11y-links-toggle" />
              <Toggle icon={Type} label="Readable font" active={prefs.readfont} onClick={() => toggle("readfont")} testid="a11y-readfont-toggle" />
              <Toggle icon={AlignJustify} label="More text spacing" active={prefs.readspace} onClick={() => toggle("readspace")} testid="a11y-readspace-toggle" />
              <Toggle icon={Pause} label="Pause animations" active={prefs.nomotion} onClick={() => toggle("nomotion")} testid="a11y-nomotion-toggle" />
              <Toggle icon={MousePointer2} label="Large cursor" active={prefs.cursor} onClick={() => toggle("cursor")} testid="a11y-cursor-toggle" />

              <button type="button" onClick={reset} disabled={!anyActive} className="flex items-center justify-center gap-2 w-full rounded-xl bg-[#0b192c] text-white px-4 py-3 text-sm font-semibold hover:bg-[#1a2a40] transition-colors disabled:opacity-40" data-testid="a11y-reset">
                <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reset all
              </button>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-3 text-center">
              <Link to="/accessibility" onClick={() => setOpen(false)} className="text-sm font-semibold text-[#d90429] hover:underline" data-testid="a11y-statement-link">
                Read our Accessibility Statement →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
